import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbortError, uploadChatDocument } from '../../../../api/agents-client/agent-client';
import type { MessageAttachment } from '../../flora.type';
import { resolveAttachmentDocType, validateAttachmentFile } from './use-agent-attachments.helpers';
import type { PendingAttachment } from './use-agent-attachments.helpers';

type UseAgentAttachmentsArgs = {
  /** The organization the chat belongs to. */
  organizationId: string;
  /** The current chat/thread id; `''` for a brand-new chat. */
  threadId: string;
  /** Sets the parent thread id when one is minted for a fresh chat. */
  setThreadId: (id: string) => void;
};

type UseAgentAttachmentsResult = {
  attachments: PendingAttachment[];
  addFiles: (files: File[]) => Promise<void>;
  removeAttachment: (localId: string) => void;
  clearAttachments: () => void;
  /** Drops everything that was just sent, but keeps failed chips on screen. */
  clearSentAttachments: () => void;
  hasUploading: boolean;
  /** Ids of the uploaded (ready) attachments, sent to the backend on submit. */
  documentIds: string[];
  /** Ready attachments as message-attachment refs, for the optimistic user bubble. */
  readyAttachments: MessageAttachment[];
};

/**
 * Stages Word/Excel attachments for the Flora composer: validates each file,
 * mints a thread id when the chat is brand-new (so the upload endpoint can
 * materialize the ChatHistory), uploads it, and tracks per-file status. The
 * minted id is pushed to the parent via `setThreadId` so the subsequent
 * WebSocket message reuses it.
 *
 * @param {UseAgentAttachmentsArgs} args - Organization id, current thread id, and its setter
 * @returns {UseAgentAttachmentsResult} Attachment state and mutators
 */
function useAgentAttachments({
  organizationId,
  threadId,
  setThreadId,
}: UseAgentAttachmentsArgs): UseAgentAttachmentsResult {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const workingThreadIdRef = useRef('');
  const attachmentsRef = useRef<PendingAttachment[]>([]);
  // In-flight upload per staged file, so removing a chip (or leaving the chat)
  // actually cancels the request instead of letting it finish and publish the
  // document to the organization-wide Documents list.
  const uploadControllersRef = useRef(new Map<string, AbortController>());
  const queryClient = useQueryClient();

  const abortUploads = useCallback((localIds?: string[]) => {
    const controllers = uploadControllersRef.current;
    const targets = localIds ?? Array.from(controllers.keys());
    for (const id of targets) {
      controllers.get(id)?.abort();
      controllers.delete(id);
    }
  }, []);

  // Apply an update to the ref and React state together. The ref is the
  // synchronous source of truth for how many slots are staged, so a concurrent
  // `addFiles` invocation sees reservations made by another before React has
  // committed them — closing the check-then-act race on `MAX_ATTACHMENTS`.
  const applyAttachments = useCallback((updater: (previous: PendingAttachment[]) => PendingAttachment[]) => {
    const next = updater(attachmentsRef.current);
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);

  useEffect(() => {
    // Preserve staged attachments only while the active thread matches the id
    // this hook is already tracking — including the id `resolveThreadId` minted
    // for a brand-new chat (pushed up via `setThreadId`, which comes back as
    // `threadId`). Any other change (New Chat clearing it to '', or navigating
    // to a different thread) belongs to a different conversation, so drop the
    // stale attachments and reset the working id rather than reuse them.
    if (threadId === workingThreadIdRef.current) return;
    workingThreadIdRef.current = threadId;
    // Cancel anything still uploading into the conversation being left, so it
    // does not land in the Documents list for a chat the user walked away from.
    abortUploads();
    attachmentsRef.current = [];
    setAttachments([]);
  }, [threadId, abortUploads]);

  // Abandoning the view mid-batch must not keep pushing files at the server.
  useEffect(() => () => abortUploads(), [abortUploads]);

  const resolveThreadId = useCallback(() => {
    if (workingThreadIdRef.current) return workingThreadIdRef.current;
    const minted = crypto.randomUUID();
    workingThreadIdRef.current = minted;
    setThreadId(minted);
    return minted;
  }, [setThreadId]);

  const addFiles = useCallback(
    async (files: File[]) => {
      // The thread this batch commits to, captured on its first upload. If the
      // active thread changes mid-batch (New Chat or navigation), later files
      // must not be uploaded into the new conversation.
      let batchThreadId = '';
      for (const file of files) {
        // Abandon the rest of the batch once the active thread no longer matches
        // the one this batch started on (an emptied ref is a New Chat reset).
        if (batchThreadId && workingThreadIdRef.current !== batchThreadId) return;
        const localId = crypto.randomUUID();
        // Count only live (non-errored) slots from the ref so reservations from
        // earlier files in this batch and from any concurrent invocation both
        // count toward the cap; rejected files never consume a slot.
        const stagedCount = attachmentsRef.current.filter((attachment) => attachment.status !== 'error').length;
        const validation = validateAttachmentFile(file, stagedCount);
        if (!validation.ok) {
          applyAttachments((previous) => [
            ...previous,
            {
              localId,
              filename: file.name,
              docType: resolveAttachmentDocType(file.name) ?? 'docx',
              sizeBytes: file.size,
              status: 'error',
              errorMessage: validation.error,
            },
          ]);
          continue;
        }
        // Reserve the slot synchronously (before awaiting the upload) so a
        // concurrent batch sees it immediately.
        applyAttachments((previous) => [
          ...previous,
          {
            localId,
            filename: file.name,
            docType: validation.docType,
            sizeBytes: file.size,
            status: 'uploading',
          },
        ]);
        const chatHistoryId = resolveThreadId();
        if (!batchThreadId) batchThreadId = chatHistoryId;
        const controller = new AbortController();
        uploadControllersRef.current.set(localId, controller);
        // The controller is released on both paths rather than in a `finally`:
        // the React Compiler cannot lower a TryStatement that has a finalizer
        // ("Handle TryStatement with a finalizer") and bails out of the whole
        // hook, which would leave `documentIds` freshly allocated every render.
        try {
          const document = await uploadChatDocument(file, chatHistoryId, organizationId, controller.signal);
          uploadControllersRef.current.delete(localId);
          applyAttachments((previous) =>
            previous.map((attachment) =>
              attachment.localId === localId ? { ...attachment, status: 'ready', documentId: document.id } : attachment,
            ),
          );
          // The Documents list caches under ['organization-documents', orgId]
          // and the app globally disables refetchOnMount, so an already-visited
          // Documents page would otherwise omit this upload until evicted.
          queryClient.invalidateQueries({ queryKey: ['organization-documents', organizationId] });
        } catch (error) {
          uploadControllersRef.current.delete(localId);
          // An abort is a deliberate cancellation, not a failure: the chip has
          // already been removed (or the whole conversation left), so there is
          // nothing to mark errored and nothing to tell the user about.
          if (isAbortError(error)) continue;
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload document';
          applyAttachments((previous) =>
            previous.map((attachment) =>
              attachment.localId === localId ? { ...attachment, status: 'error', errorMessage } : attachment,
            ),
          );
        }
      }
    },
    [applyAttachments, organizationId, queryClient, resolveThreadId],
  );

  const removeAttachment = useCallback(
    (localId: string) => {
      abortUploads([localId]);
      applyAttachments((previous) => previous.filter((attachment) => attachment.localId !== localId));
    },
    [abortUploads, applyAttachments],
  );

  const clearAttachments = useCallback(() => {
    abortUploads();
    applyAttachments(() => []);
  }, [abortUploads, applyAttachments]);

  const clearSentAttachments = useCallback(() => {
    // Keep failed chips in the composer after a send. Only `ready` attachments
    // actually ride along on the message, so wiping the whole tray would delete
    // the one piece of evidence that a file did NOT reach Flora — the user would
    // watch their error chip disappear as the message goes and reasonably
    // conclude the file was attached.
    applyAttachments((previous) => previous.filter((attachment) => attachment.status === 'error'));
  }, [applyAttachments]);

  const readyAttachments = attachments.filter(
    (attachment): attachment is PendingAttachment & { documentId: string } =>
      attachment.status === 'ready' && !!attachment.documentId,
  );

  return {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    clearSentAttachments,
    hasUploading: attachments.some((attachment) => attachment.status === 'uploading'),
    documentIds: readyAttachments.map((attachment) => attachment.documentId),
    readyAttachments: readyAttachments.map((attachment) => ({
      document_id: attachment.documentId,
      filename: attachment.filename,
      doc_type: attachment.docType,
    })),
  };
}

/**
 * The subset of attachment state threaded down to the composer view.
 */
type AgentAttachmentsBinding = {
  items: PendingAttachment[];
  addFiles: (files: File[]) => void;
  removeAttachment: (localId: string) => void;
  hasUploading: boolean;
};

export { useAgentAttachments };
export type { AgentAttachmentsBinding, UseAgentAttachmentsResult };
